/**
 * DamageReporting - Manage room damage reports and inspections
 * Features:
 * - Report damage with photos
 * - Track repair status
 * - Notify guests of charges
 * - Room inspections with checklists
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  AlertTriangle, Camera, Plus, Send, Trash2, RefreshCw,
  ClipboardCheck, DollarSign, Eye, Pencil, X, Upload, Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DamageReporting = () => {
  const [activeTab, setActiveTab] = useState('reports');
  const [damageReports, setDamageReports] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [showInspectionDialog, setShowInspectionDialog] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  // Form state for damage report
  const [reportForm, setReportForm] = useState({
    room_id: '',
    title: '',
    description: '',
    damage_type: 'general',
    severity: 'minor',
    estimated_cost: 0,
    photos: []
  });

  // Form state for inspection
  const [inspectionForm, setInspectionForm] = useState({
    room_id: '',
    inspection_type: 'daily',
    overall_rating: 5,
    notes: '',
    photos: [],
    issues_found: [],
    checklist: {
      cleanliness: 'good',
      furniture: 'good',
      fixtures: 'good',
      appliances: 'good',
      bathroom: 'good',
      bedding: 'good',
      walls: 'good',
      flooring: 'good'
    }
  });

  const [newIssue, setNewIssue] = useState('');

  const getToken = () => {
    try {
      const authStorage = localStorage.getItem('eventflow-auth');
      if (authStorage) {
        const parsed = JSON.parse(authStorage);
        return parsed?.state?.token || localStorage.getItem('eventflow_token');
      }
    } catch (e) {}
    return localStorage.getItem('eventflow_token');
  };

  const fetchData = async () => {
    setLoading(true);
    const token = getToken();
    const headers = { 'Authorization': `Bearer ${token}` };

    try {
      const [reportsRes, inspectionsRes, roomsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/accommodation/damage-reports`, { headers }),
        fetch(`${API_URL}/api/admin/accommodation/inspections`, { headers }),
        fetch(`${API_URL}/api/admin/accommodation/rooms`, { headers })
      ]);

      if (reportsRes.ok) setDamageReports(await reportsRes.json());
      if (inspectionsRes.ok) setInspections(await inspectionsRes.json());
      if (roomsRes.ok) setRooms(await roomsRes.json());
    } catch (err) {
      console.error('Failed to fetch data:', err);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handlePhotoUpload = async (e, formType) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingPhoto(true);
    const token = getToken();
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await fetch(`${API_URL}/api/admin/accommodation/upload-photo`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (res.ok) {
        const data = await res.json();
        if (formType === 'report') {
          setReportForm(prev => ({ ...prev, photos: [...prev.photos, data.url] }));
        } else {
          setInspectionForm(prev => ({ ...prev, photos: [...prev.photos, data.url] }));
        }
        toast.success('Photo uploaded');
      } else {
        toast.error('Upload failed');
      }
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const removePhoto = (index, formType) => {
    if (formType === 'report') {
      setReportForm(prev => ({
        ...prev,
        photos: prev.photos.filter((_, i) => i !== index)
      }));
    } else {
      setInspectionForm(prev => ({
        ...prev,
        photos: prev.photos.filter((_, i) => i !== index)
      }));
    }
  };

  const submitDamageReport = async () => {
    if (!reportForm.room_id || !reportForm.title) {
      toast.error('Please fill in required fields');
      return;
    }

    const token = getToken();
    try {
      const method = editingReport ? 'PUT' : 'POST';
      const url = editingReport 
        ? `${API_URL}/api/admin/accommodation/damage-reports/${editingReport.id}`
        : `${API_URL}/api/admin/accommodation/damage-reports`;

      const res = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(reportForm)
      });

      if (res.ok) {
        toast.success(editingReport ? 'Report updated' : 'Damage reported');
        setShowReportDialog(false);
        setEditingReport(null);
        resetReportForm();
        fetchData();
      } else {
        toast.error('Failed to save report');
      }
    } catch (err) {
      toast.error('Failed to save report');
    }
  };

  const submitInspection = async () => {
    if (!inspectionForm.room_id) {
      toast.error('Please select a room');
      return;
    }

    const token = getToken();
    try {
      const res = await fetch(`${API_URL}/api/admin/accommodation/inspections`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(inspectionForm)
      });

      if (res.ok) {
        toast.success('Inspection recorded');
        setShowInspectionDialog(false);
        resetInspectionForm();
        fetchData();
      } else {
        toast.error('Failed to save inspection');
      }
    } catch (err) {
      toast.error('Failed to save inspection');
    }
  };

  const notifyGuest = async (reportId) => {
    const token = getToken();
    try {
      const res = await fetch(`${API_URL}/api/admin/accommodation/damage-reports/${reportId}/notify-guest`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || 'Notification sent');
        fetchData();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to send notification');
      }
    } catch (err) {
      toast.error('Failed to send notification');
    }
  };

  const sendInspectionReport = async (inspectionId) => {
    const token = getToken();
    try {
      const res = await fetch(`${API_URL}/api/admin/accommodation/inspections/${inspectionId}/send-report`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || 'Report sent');
        fetchData();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to send report');
      }
    } catch (err) {
      toast.error('Failed to send report');
    }
  };

  const deleteReport = async (reportId) => {
    if (!confirm('Are you sure you want to delete this report?')) return;

    const token = getToken();
    try {
      const res = await fetch(`${API_URL}/api/admin/accommodation/damage-reports/${reportId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Report deleted');
        fetchData();
      } else {
        toast.error('Failed to delete report');
      }
    } catch (err) {
      toast.error('Failed to delete report');
    }
  };

  const resetReportForm = () => {
    setReportForm({
      room_id: '',
      title: '',
      description: '',
      damage_type: 'general',
      severity: 'minor',
      estimated_cost: 0,
      photos: []
    });
  };

  const resetInspectionForm = () => {
    setInspectionForm({
      room_id: '',
      inspection_type: 'daily',
      overall_rating: 5,
      notes: '',
      photos: [],
      issues_found: [],
      checklist: {
        cleanliness: 'good',
        furniture: 'good',
        fixtures: 'good',
        appliances: 'good',
        bathroom: 'good',
        bedding: 'good',
        walls: 'good',
        flooring: 'good'
      }
    });
  };

  const addIssue = () => {
    if (!newIssue.trim()) return;
    setInspectionForm(prev => ({
      ...prev,
      issues_found: [...prev.issues_found, newIssue.trim()]
    }));
    setNewIssue('');
  };

  const removeIssue = (index) => {
    setInspectionForm(prev => ({
      ...prev,
      issues_found: prev.issues_found.filter((_, i) => i !== index)
    }));
  };

  const getSeverityColor = (severity) => {
    const colors = {
      minor: 'bg-yellow-100 text-yellow-800',
      moderate: 'bg-orange-100 text-orange-800',
      major: 'bg-red-100 text-red-800',
      critical: 'bg-red-200 text-red-900'
    };
    return colors[severity] || 'bg-gray-100 text-gray-800';
  };

  const getStatusColor = (status) => {
    const colors = {
      reported: 'bg-blue-100 text-blue-800',
      assessed: 'bg-purple-100 text-purple-800',
      repair_scheduled: 'bg-yellow-100 text-yellow-800',
      repaired: 'bg-green-100 text-green-800',
      charged: 'bg-orange-100 text-orange-800',
      closed: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Damage Reports & Inspections</h2>
          <p className="text-muted-foreground">Track room damage and conduct inspections</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchData} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-1" />
            Refresh
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="reports" className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            Damage Reports ({damageReports.length})
          </TabsTrigger>
          <TabsTrigger value="inspections" className="flex items-center gap-2">
            <ClipboardCheck className="h-4 w-4" />
            Inspections ({inspections.length})
          </TabsTrigger>
        </TabsList>

        {/* Damage Reports Tab */}
        <TabsContent value="reports" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => { resetReportForm(); setEditingReport(null); setShowReportDialog(true); }}>
              <Plus className="h-4 w-4 mr-1" />
              Report Damage
            </Button>
          </div>

          <div className="grid gap-4">
            {damageReports.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-muted-foreground">
                  <AlertTriangle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No damage reports found</p>
                </CardContent>
              </Card>
            ) : (
              damageReports.map((report) => (
                <Card key={report.id}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-semibold">{report.title}</h3>
                          <Badge className={getSeverityColor(report.severity)}>
                            {report.severity}
                          </Badge>
                          <Badge className={getStatusColor(report.status)}>
                            {report.status?.replace('_', ' ')}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mb-2">{report.description}</p>
                        <div className="flex flex-wrap gap-4 text-sm">
                          <span>Room: <strong>{report.room_number || 'N/A'}</strong></span>
                          <span>Type: <strong>{report.damage_type}</strong></span>
                          <span>Est. Cost: <strong>${report.estimated_cost?.toLocaleString() || 0}</strong></span>
                          {report.guest_name && (
                            <span>Guest: <strong>{report.guest_name}</strong></span>
                          )}
                        </div>
                        {report.photos?.length > 0 && (
                          <div className="flex gap-2 mt-3">
                            {report.photos.slice(0, 4).map((photo, i) => (
                              <img 
                                key={i} 
                                src={photo.startsWith('/api') ? `${API_URL}${photo}` : photo} 
                                alt={`Damage ${i + 1}`}
                                className="w-16 h-16 object-cover rounded"
                              />
                            ))}
                            {report.photos.length > 4 && (
                              <div className="w-16 h-16 bg-muted rounded flex items-center justify-center text-sm">
                                +{report.photos.length - 4}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2 ml-4">
                        {report.guest_email && !report.guest_notified && (
                          <Button size="sm" variant="outline" onClick={() => notifyGuest(report.id)}>
                            <Send className="h-4 w-4 mr-1" />
                            Notify Guest
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => deleteReport(report.id)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* Inspections Tab */}
        <TabsContent value="inspections" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => { resetInspectionForm(); setShowInspectionDialog(true); }}>
              <Plus className="h-4 w-4 mr-1" />
              New Inspection
            </Button>
          </div>

          <div className="grid gap-4">
            {inspections.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-muted-foreground">
                  <ClipboardCheck className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No inspections found</p>
                </CardContent>
              </Card>
            ) : (
              inspections.map((inspection) => (
                <Card key={inspection.id}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-semibold">Room {inspection.room_number || 'N/A'}</h3>
                          <Badge variant="outline">{inspection.inspection_type} inspection</Badge>
                          <span className="text-yellow-500">
                            {'⭐'.repeat(inspection.overall_rating)}{'☆'.repeat(5 - inspection.overall_rating)}
                          </span>
                        </div>
                        {inspection.notes && (
                          <p className="text-sm text-muted-foreground mb-2">{inspection.notes}</p>
                        )}
                        {inspection.issues_found?.length > 0 && (
                          <div className="mb-2">
                            <span className="text-sm font-medium text-red-600">Issues Found:</span>
                            <ul className="list-disc list-inside text-sm text-red-600">
                              {inspection.issues_found.map((issue, i) => (
                                <li key={i}>{issue}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        <p className="text-xs text-muted-foreground">
                          Inspected by {inspection.inspected_by} on {new Date(inspection.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex gap-2 ml-4">
                        {inspection.guest_id && !inspection.report_sent && (
                          <Button size="sm" variant="outline" onClick={() => sendInspectionReport(inspection.id)}>
                            <Send className="h-4 w-4 mr-1" />
                            Send Report
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Damage Report Dialog */}
      <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              {editingReport ? 'Edit Damage Report' : 'Report Damage'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Room *</Label>
                <Select value={reportForm.room_id} onValueChange={(v) => setReportForm({ ...reportForm, room_id: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select room" />
                  </SelectTrigger>
                  <SelectContent>
                    {rooms.map((room) => (
                      <SelectItem key={room.id} value={room.id}>
                        {room.room_number} - {room.building_name || 'Building'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Severity</Label>
                <Select value={reportForm.severity} onValueChange={(v) => setReportForm({ ...reportForm, severity: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="minor">Minor</SelectItem>
                    <SelectItem value="moderate">Moderate</SelectItem>
                    <SelectItem value="major">Major</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Title *</Label>
              <Input
                value={reportForm.title}
                onChange={(e) => setReportForm({ ...reportForm, title: e.target.value })}
                placeholder="Brief description of damage"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Damage Type</Label>
                <Select value={reportForm.damage_type} onValueChange={(v) => setReportForm({ ...reportForm, damage_type: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="furniture">Furniture</SelectItem>
                    <SelectItem value="fixtures">Fixtures</SelectItem>
                    <SelectItem value="appliances">Appliances</SelectItem>
                    <SelectItem value="bathroom">Bathroom</SelectItem>
                    <SelectItem value="walls">Walls</SelectItem>
                    <SelectItem value="flooring">Flooring</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Estimated Cost ($)</Label>
                <Input
                  type="number"
                  value={reportForm.estimated_cost}
                  onChange={(e) => setReportForm({ ...reportForm, estimated_cost: parseFloat(e.target.value) || 0 })}
                  placeholder="0.00"
                />
              </div>
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                value={reportForm.description}
                onChange={(e) => setReportForm({ ...reportForm, description: e.target.value })}
                placeholder="Detailed description of the damage..."
                rows={3}
              />
            </div>

            <div>
              <Label>Photos</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {reportForm.photos.map((photo, i) => (
                  <div key={i} className="relative">
                    <img 
                      src={photo.startsWith('/api') ? `${API_URL}${photo}` : photo} 
                      alt={`Photo ${i + 1}`}
                      className="w-20 h-20 object-cover rounded"
                    />
                    <button
                      onClick={() => removePhoto(i, 'report')}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
                <label className="w-20 h-20 border-2 border-dashed rounded flex items-center justify-center cursor-pointer hover:bg-muted">
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handlePhotoUpload(e, 'report')}
                    disabled={uploadingPhoto}
                  />
                  {uploadingPhoto ? (
                    <Loader2 className="h-6 w-6 animate-spin" />
                  ) : (
                    <Camera className="h-6 w-6 text-muted-foreground" />
                  )}
                </label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReportDialog(false)}>Cancel</Button>
            <Button onClick={submitDamageReport} className="bg-red-600 hover:bg-red-700">
              {editingReport ? 'Update Report' : 'Submit Report'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Inspection Dialog */}
      <Dialog open={showInspectionDialog} onOpenChange={setShowInspectionDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardCheck className="h-5 w-5 text-blue-500" />
              New Room Inspection
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Room *</Label>
                <Select value={inspectionForm.room_id} onValueChange={(v) => setInspectionForm({ ...inspectionForm, room_id: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select room" />
                  </SelectTrigger>
                  <SelectContent>
                    {rooms.map((room) => (
                      <SelectItem key={room.id} value={room.id}>
                        {room.room_number} - {room.building_name || 'Building'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Inspection Type</Label>
                <Select value={inspectionForm.inspection_type} onValueChange={(v) => setInspectionForm({ ...inspectionForm, inspection_type: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="checkout">Check-out</SelectItem>
                    <SelectItem value="maintenance">Maintenance</SelectItem>
                    <SelectItem value="deep_clean">Deep Clean</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Checklist</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {Object.entries(inspectionForm.checklist).map(([item, value]) => (
                  <div key={item} className="flex items-center justify-between p-2 border rounded">
                    <span className="text-sm capitalize">{item.replace('_', ' ')}</span>
                    <Select 
                      value={value} 
                      onValueChange={(v) => setInspectionForm({ 
                        ...inspectionForm, 
                        checklist: { ...inspectionForm.checklist, [item]: v } 
                      })}
                    >
                      <SelectTrigger className="w-24 h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="excellent">Excellent</SelectItem>
                        <SelectItem value="good">Good</SelectItem>
                        <SelectItem value="fair">Fair</SelectItem>
                        <SelectItem value="poor">Poor</SelectItem>
                        <SelectItem value="needs_attention">Needs Attention</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label>Overall Rating</Label>
              <div className="flex items-center gap-2 mt-2">
                {[1, 2, 3, 4, 5].map((rating) => (
                  <button
                    key={rating}
                    onClick={() => setInspectionForm({ ...inspectionForm, overall_rating: rating })}
                    className={`text-2xl ${rating <= inspectionForm.overall_rating ? 'text-yellow-400' : 'text-gray-300'}`}
                  >
                    ★
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label>Issues Found</Label>
              <div className="flex gap-2 mt-2">
                <Input
                  value={newIssue}
                  onChange={(e) => setNewIssue(e.target.value)}
                  placeholder="Describe issue..."
                  onKeyPress={(e) => e.key === 'Enter' && addIssue()}
                />
                <Button onClick={addIssue} size="sm">Add</Button>
              </div>
              {inspectionForm.issues_found.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {inspectionForm.issues_found.map((issue, i) => (
                    <li key={i} className="flex items-center justify-between p-2 bg-red-50 rounded text-sm">
                      <span>{issue}</span>
                      <button onClick={() => removeIssue(i)} className="text-red-500">
                        <X className="h-4 w-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea
                value={inspectionForm.notes}
                onChange={(e) => setInspectionForm({ ...inspectionForm, notes: e.target.value })}
                placeholder="Additional notes..."
                rows={2}
              />
            </div>

            <div>
              <Label>Photos</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {inspectionForm.photos.map((photo, i) => (
                  <div key={i} className="relative">
                    <img 
                      src={photo.startsWith('/api') ? `${API_URL}${photo}` : photo} 
                      alt={`Photo ${i + 1}`}
                      className="w-20 h-20 object-cover rounded"
                    />
                    <button
                      onClick={() => removePhoto(i, 'inspection')}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
                <label className="w-20 h-20 border-2 border-dashed rounded flex items-center justify-center cursor-pointer hover:bg-muted">
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handlePhotoUpload(e, 'inspection')}
                    disabled={uploadingPhoto}
                  />
                  {uploadingPhoto ? (
                    <Loader2 className="h-6 w-6 animate-spin" />
                  ) : (
                    <Camera className="h-6 w-6 text-muted-foreground" />
                  )}
                </label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInspectionDialog(false)}>Cancel</Button>
            <Button onClick={submitInspection} className="bg-blue-600 hover:bg-blue-700">
              Submit Inspection
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DamageReporting;
